import React from "react";
import { trackError } from "../../../../lib/metrics";
import { useJ2PuzzleState } from "../../../../hooks/useJ2PuzzleState";

const ZONES = [
  { id: "A", name: "Cortex préfrontal médian", color: "cyan", connections: [1] },
  { id: "B", name: "Jonction temporo-pariétale", color: "fuchsia", connections: [0, 3] },
  { id: "C", name: "Cortex cingulaire antérieur", color: "amber", connections: [3] },
  { id: "D", name: "Insula antérieure", color: "emerald", connections: [1, 2] },
  { id: "E", name: "Cortex moteur primaire", color: "rose", connections: [2] },
  { id: "F", name: "Cortex visuel primaire", color: "violet", connections: [1, 2] },
];

const CORRECT_SEQUENCE = ["A", "B", "D", "C"];

const colorMap = {
  cyan: { bg: "bg-cyan-500/20", border: "border-cyan-400", text: "text-cyan-300", glow: "shadow-[0_0_20px_rgba(34,211,238,0.5)]", hover: "hover:shadow-[0_0_30px_rgba(34,211,238,0.8)]" },
  fuchsia: { bg: "bg-fuchsia-500/20", border: "border-fuchsia-400", text: "text-fuchsia-300", glow: "shadow-[0_0_20px_rgba(232,121,249,0.5)]", hover: "hover:shadow-[0_0_30px_rgba(232,121,249,0.8)]" },
  amber: { bg: "bg-amber-500/20", border: "border-amber-400", text: "text-amber-300", glow: "shadow-[0_0_20px_rgba(251,191,36,0.5)]", hover: "hover:shadow-[0_0_30px_rgba(251,191,36,0.8)]" },
  emerald: { bg: "bg-emerald-500/20", border: "border-emerald-400", text: "text-emerald-300", glow: "shadow-[0_0_20px_rgba(52,211,153,0.5)]", hover: "hover:shadow-[0_0_30px_rgba(52,211,153,0.8)]" },
  rose: { bg: "bg-rose-500/20", border: "border-rose-400", text: "text-rose-300", glow: "shadow-[0_0_20px_rgba(251,113,133,0.5)]", hover: "hover:shadow-[0_0_30px_rgba(251,113,133,0.8)]" },
  violet: { bg: "bg-violet-500/20", border: "border-violet-400", text: "text-violet-300", glow: "shadow-[0_0_20px_rgba(167,139,250,0.5)]", hover: "hover:shadow-[0_0_30px_rgba(167,139,250,0.8)]" },
};

export default function BrainTerminalModal({ onSuccess, code, playerId }) {
  const [puzzleState, setPuzzleState, loaded] = useJ2PuzzleState({
    code,
    playerId,
    puzzleType: "brainTerminal",
    initialState: { sequence: [], status: "idle" },
  });

  if (!loaded) {
    return <div className="p-4 text-center text-slate-400">Chargement...</div>;
  }

  const handleZoneClick = (zoneId) => {
    if (puzzleState.status === "success") return;

    const newSeq = [...puzzleState.sequence, zoneId];
    setPuzzleState({ sequence: newSeq, status: "idle" });

    if (newSeq.length === CORRECT_SEQUENCE.length) {
      const isCorrect = newSeq.every((zone, idx) => zone === CORRECT_SEQUENCE[idx]);

      if (isCorrect) {
        setPuzzleState({ sequence: newSeq, status: "success" });
        setTimeout(() => { onSuccess?.(); }, 1200);
      } else {
        setPuzzleState({ sequence: newSeq, status: "error" });
        // ── trackError ──
        trackError({
          sessionCode: code,
          stage: 2,
          playerId,
          reason: "brain_terminal_wrong_sequence",
          meta: { entered: newSeq.join("") },
        });
        setTimeout(() => { setPuzzleState({ sequence: [], status: "idle" }); }, 1500);
      }
    }
  };

  const handleReset = () => {
    setPuzzleState({ sequence: [], status: "idle" });
  };

  return (
    <div className="flex flex-col items-center gap-6 py-6 px-4 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="text-center space-y-2">
        <div className="text-sm uppercase tracking-[0.25em] text-slate-400 flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-fuchsia-500 rounded-full animate-pulse"></div>
          Terminal de calibration cognitive
          <div className="w-2 h-2 bg-cyan-500 rounded-full animate-pulse" style={{ animationDelay: "0.5s" }}></div>
        </div>
        <div className="text-xs text-slate-500 font-mono">NEURAL NETWORK ACTIVATION PROTOCOL</div>
      </div>

      <div className="w-full max-w-3xl space-y-6">
        {/* Neural sequence display */}
        <div className="relative p-6 bg-black/60 border border-white/10 rounded-xl backdrop-blur">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-1 h-8 bg-gradient-to-b from-cyan-500 to-fuchsia-500 rounded"></div>
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-400 font-semibold">Séquence d'activation neurale</div>
                <div className="text-[10px] text-slate-600 font-mono mt-0.5">{puzzleState.sequence.length} / {CORRECT_SEQUENCE.length} zones activées</div>
              </div>
            </div>
            <button onClick={handleReset} className="px-4 py-2 rounded-lg bg-slate-800/80 border border-slate-600 text-xs text-slate-300 font-medium hover:bg-slate-700 hover:border-slate-500 hover:text-white transition-all duration-200 shadow-lg">
              ⟲ Réinitialiser
            </button>
          </div>

          <div className="flex gap-3 h-20 items-center">
            {CORRECT_SEQUENCE.map((_, idx) => {
              const activated = puzzleState.sequence[idx];
              const zone = activated ? ZONES.find((z) => z.id === activated) : null;
              const colors = zone ? colorMap[zone.color] : null;
              return (
                <React.Fragment key={idx}>
                  <div className={`flex-1 h-full rounded-lg border-2 flex flex-col items-center justify-center text-lg font-bold transition-all duration-300 ${activated ? `${colors.bg} ${colors.border} ${colors.text} ${colors.glow}` : "bg-slate-900/50 border-slate-700/50 text-slate-600"}`}>
                    <div className="text-2xl font-mono">{activated || "—"}</div>
                    {activated && <div className="text-[9px] uppercase tracking-wider opacity-60 mt-1">{zone.name.split(" ")[0]}</div>}
                  </div>
                  {idx < CORRECT_SEQUENCE.length - 1 && (
                    <div className="flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" className={puzzleState.sequence[idx + 1] ? "text-fuchsia-400" : "text-slate-700"}>
                        <path fill="currentColor" d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                      </svg>
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {puzzleState.status === "error" && (
            <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-red-400 text-sm font-semibold">
                <span className="text-lg">⚠</span>
                <span>Séquence incorrecte détectée</span>
              </div>
              <div className="text-xs text-red-400/70 mt-1 ml-7">Réinitialisation automatique en cours...</div>
            </div>
          )}

          {puzzleState.status === "success" && (
            <div className="mt-4 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
              <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                <span className="text-lg">✓</span>
                <span>Calibration réussie — Module activé</span>
              </div>
              <div className="text-xs text-emerald-400/70 mt-1 ml-7">Accès aux données cognitives déverrouillé</div>
            </div>
          )}
        </div>

        {/* Neural network visualization */}
        <div className="relative">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500/5 via-fuchsia-500/5 to-emerald-500/5 rounded-xl blur-xl"></div>
          <div className="relative grid grid-cols-2 gap-4">
            {ZONES.map((zone) => {
              const isActive = puzzleState.sequence.includes(zone.id);
              const colors = colorMap[zone.color];
              const isDisabled = puzzleState.status === "success";
              return (
                <button key={zone.id} onClick={() => handleZoneClick(zone.id)} disabled={isDisabled}
                  className={`group relative p-6 rounded-xl border-2 transition-all duration-200 ${colors.bg} ${colors.border} ${isActive ? "opacity-40 cursor-not-allowed scale-95" : isDisabled ? "opacity-40 cursor-not-allowed" : `${colors.hover} hover:scale-105 cursor-pointer`}`}>
                  <div className="absolute inset-0 opacity-10">
                    <svg width="100%" height="100%" className={colors.text}>
                      <pattern id={`pattern-${zone.id}`} x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
                        <circle cx="2" cy="2" r="1" fill="currentColor" />
                      </pattern>
                      <rect width="100%" height="100%" fill={`url(#pattern-${zone.id})`} />
                    </svg>
                  </div>
                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`text-3xl font-bold ${colors.text} font-mono`}>{zone.id}</div>
                      <div className={`w-8 h-8 rounded-full border-2 ${colors.border} flex items-center justify-center ${isActive ? colors.bg : "bg-transparent"} transition-all duration-300`}>
                        {isActive && <div className={`w-3 h-3 rounded-full ${colors.text.replace("text", "bg")}`}></div>}
                      </div>
                    </div>
                    <div className={`text-sm ${colors.text} font-medium mb-1`}>{zone.name}</div>
                    <div className="flex gap-1 mt-3">
                      {[0, 1, 2, 3].map((i) => (
                        <div key={i} className={`h-1 flex-1 rounded-full ${zone.connections.includes(i) ? colors.border.replace("border", "bg") : "bg-slate-800"} ${!isDisabled && !isActive ? "group-hover:opacity-100" : ""} opacity-40 transition-opacity`}></div>
                      ))}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900/50 border border-slate-700/50 rounded-full">
            <svg className="w-4 h-4 text-slate-500" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <span className="text-xs text-slate-500">Activez les zones cérébrales dans l'ordre indiqué sur le schéma du tableau blanc</span>
          </div>
        </div>
      </div>
    </div>
  );
}